import { useEffect, useState } from "react";
import { api, resolveMediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, Plus, Copy, Trash2, Sparkles, UserPlus, Baby } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { openUpgradeModalFromError } from "@/components/UpgradeModal";

export default function Family() {
  const { user } = useAuth();
  const [data, setData] = useState({ family: null, members: [], children: [] });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [openChild, setOpenChild] = useState(false);
  const [childDraft, setChildDraft] = useState({ name: "", age: "", gender: "", height_cm: "", weight_kg: "", current_size: "", shoe_size: "" });
  const [forecasts, setForecasts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/family");
      setData(res);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/family", {});
      await load();
      toast.success("Family circle created");
    } catch (e) {
      // 402 → needs Family plan or Premium → open upgrade modal.
      if (e?.response?.status === 402 && openUpgradeModalFromError(e, "family")) {
        return;
      }
      toast.error(e?.response?.data?.detail?.message || "Could not create family");
    }
    finally { setCreating(false); }
  };

  const join = async () => {
    if (!joinCode.trim()) return;
    try {
      await api.post("/family/join", { invite_code: joinCode.trim() });
      setJoinCode("");
      await load();
      toast.success("Joined family");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid invite code"); }
  };

  const addChild = async () => {
    if (!childDraft.name || !childDraft.age) { toast.error("Name and age required"); return; }
    try {
      await api.post("/family/children", {
        ...childDraft,
        age: Number(childDraft.age),
        height_cm: childDraft.height_cm ? Number(childDraft.height_cm) : null,
        weight_kg: childDraft.weight_kg ? Number(childDraft.weight_kg) : null,
      });
      setOpenChild(false);
      setChildDraft({ name: "", age: "", gender: "", height_cm: "", weight_kg: "", current_size: "", shoe_size: "" });
      await load();
    } catch (e) { toast.error("Could not add child"); }
  };

  const removeChild = async (id) => {
    await api.delete(`/family/children/${id}`);
    await load();
  };

  const forecast = async (id) => {
    setForecasts((f) => ({ ...f, [id]: "loading" }));
    try {
      const { data } = await api.get(`/family/children/${id}/forecast?months=6`);
      setForecasts((f) => ({ ...f, [id]: data }));
    } catch {
      setForecasts((f) => ({ ...f, [id]: null }));
      toast.error("Forecast failed");
    }
  };

  const copyInvite = () => {
    navigator.clipboard.writeText(data.family?.invite_code || "");
    toast.success("Invite code copied");
  };

  const isOwner = data.family?.owner_id === user?.user_id;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <p className="eyebrow">Family · Shared atelier</p>
      <h1 className="editorial-h1 mt-3">Dress the household.</h1>

      {!data.family && (
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <div className="border border-border p-8">
            <p className="eyebrow">Start a family</p>
            <p className="font-serif text-3xl mt-3">Create a circle.</p>
            <p className="text-sm text-muted-foreground mt-3">Invite spouse and parents; add child profiles. Your Premium covers them.</p>
            <Button
              data-testid="family-create-btn"
              disabled={creating}
              onClick={create}
              className="mt-6 rounded-none uppercase tracking-[0.2em] text-xs h-11"
            >{creating ? "Creating…" : "Create family"}</Button>
          </div>
          <div className="border border-border p-8">
            <p className="eyebrow">Have an invite?</p>
            <p className="font-serif text-3xl mt-3">Join the circle.</p>
            <div className="mt-5 flex gap-2">
              <Input
                data-testid="family-invite-input"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="INVITE CODE"
                className="rounded-none h-11 uppercase tracking-[0.2em]"
              />
              <Button onClick={join} data-testid="family-join-btn" className="rounded-none h-11 uppercase tracking-[0.2em] text-xs">Join</Button>
            </div>
          </div>
        </div>
      )}

      {data.family && (
        <>
          <div className="mt-10 grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 border border-border p-8">
              <p className="eyebrow">Your family</p>
              <p className="font-serif text-4xl mt-3 tracking-tight">{data.family.name}</p>
              <div className="mt-6 flex items-center gap-3">
                <code className="px-3 py-2 border border-border text-sm tracking-[0.3em]">{data.family.invite_code}</code>
                <Button data-testid="family-copy-code" variant="outline" onClick={copyInvite} className="rounded-none uppercase tracking-[0.2em] text-[10px]">
                  <Copy size={12} className="mr-1.5" /> Copy invite
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Share this code with spouse/parents. They sign in with Google and tap "Join".</p>

              <div className="mt-8">
                <p className="eyebrow mb-3">Members</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {data.members.map((m) => (
                    <div key={m.user_id} className="border border-border p-4 flex items-center gap-3">
                      <div className="h-10 w-10 border border-border overflow-hidden">
                        {m.picture ? <img src={resolveMediaUrl(m.picture)} alt="" className="h-full w-full object-cover" /> : <span className="text-xs flex items-center justify-center h-full">{m.name?.[0]}</span>}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm truncate">{m.name}</p>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground truncate">{m.user_id === data.family.owner_id ? "Owner" : "Member"}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 border border-border p-8 bg-accent text-accent-foreground">
              <p className="text-[10px] uppercase tracking-[0.3em] opacity-80">Family Premium</p>
              <p className="font-serif text-3xl mt-3">All members covered.</p>
              <ul className="mt-5 space-y-2 text-sm opacity-90">
                <li>· Shared Premium access across the household</li>
                <li>· Individual wardrobes for each member</li>
                <li>· Child profiles with growth intelligence</li>
                <li>· One bill, one circle</li>
              </ul>
            </div>
          </div>

          {isOwner && (
            <div className="mt-12">
              <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                  <p className="eyebrow flex items-center gap-2"><Baby size={12} /> Children</p>
                  <h2 className="editorial-h2 mt-2">Their wardrobe, your peace.</h2>
                </div>
                <Dialog open={openChild} onOpenChange={setOpenChild}>
                  <DialogTrigger asChild>
                    <Button data-testid="family-add-child-btn" className="rounded-none uppercase tracking-[0.2em] text-xs h-11">
                      <UserPlus size={14} className="mr-2" /> Add child
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-none">
                    <DialogHeader><DialogTitle className="font-serif text-2xl">New child profile</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <Field label="Name"><Input data-testid="child-name-input" value={childDraft.name} onChange={(e) => setChildDraft({ ...childDraft, name: e.target.value })} className="rounded-none" /></Field>
                      <Field label="Age"><Input data-testid="child-age-input" type="number" value={childDraft.age} onChange={(e) => setChildDraft({ ...childDraft, age: e.target.value })} className="rounded-none" /></Field>
                      <Field label="Gender">
                        <Select value={childDraft.gender} onValueChange={(v) => setChildDraft({ ...childDraft, gender: v })}>
                          <SelectTrigger className="rounded-none"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="girl">Girl</SelectItem>
                            <SelectItem value="boy">Boy</SelectItem>
                            <SelectItem value="non-binary">Non-binary</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Current size"><Input value={childDraft.current_size} onChange={(e) => setChildDraft({ ...childDraft, current_size: e.target.value })} placeholder="e.g. 6Y, S" className="rounded-none" /></Field>
                      <Field label="Height (cm)"><Input type="number" value={childDraft.height_cm} onChange={(e) => setChildDraft({ ...childDraft, height_cm: e.target.value })} className="rounded-none" /></Field>
                      <Field label="Weight (kg)"><Input type="number" value={childDraft.weight_kg} onChange={(e) => setChildDraft({ ...childDraft, weight_kg: e.target.value })} className="rounded-none" /></Field>
                      <Field label="Shoe size"><Input value={childDraft.shoe_size} onChange={(e) => setChildDraft({ ...childDraft, shoe_size: e.target.value })} className="rounded-none" /></Field>
                    </div>
                    <Button onClick={addChild} data-testid="child-save-btn" className="rounded-none mt-4 h-11 uppercase tracking-[0.2em] text-xs">Save child</Button>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.children.map((c) => {
                  const f = forecasts[c.id];
                  return (
                    <motion.div
                      key={c.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border border-border p-6 bg-card"
                      data-testid={`child-card-${c.id}`}
                    >
                      <div className="flex items-baseline justify-between">
                        <p className="font-serif text-3xl tracking-tight">{c.name}</p>
                        <button onClick={() => removeChild(c.id)} className="opacity-60 hover:opacity-100"><Trash2 size={14} /></button>
                      </div>
                      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">{c.age} years · {c.gender || "—"}</p>
                      <div className="mt-3 text-xs grid grid-cols-2 gap-y-1.5 text-muted-foreground">
                        <span>Height: {c.height_cm ? `${c.height_cm}cm` : "—"}</span>
                        <span>Weight: {c.weight_kg ? `${c.weight_kg}kg` : "—"}</span>
                        <span>Size: {c.current_size || "—"}</span>
                        <span>Shoes: {c.shoe_size || "—"}</span>
                      </div>
                      <Button
                        data-testid={`child-forecast-${c.id}`}
                        variant="outline"
                        onClick={() => forecast(c.id)}
                        disabled={f === "loading"}
                        className="rounded-none w-full mt-5 uppercase tracking-[0.2em] text-[10px] h-10"
                      >
                        {f === "loading" ? <Loader2 size={12} className="animate-spin" /> : <><Sparkles size={12} className="mr-2" /> 6-month growth forecast</>}
                      </Button>
                      {f && f !== "loading" && (
                        <div className="mt-4 border-t border-border pt-3 text-xs space-y-2">
                          <p className="text-muted-foreground italic">{f.growth_insights}</p>
                          {(f.predicted_sizes || []).map((p, i) => (
                            <div key={i} className="flex justify-between">
                              <span className="text-muted-foreground">+{p.months_from_now} mo</span>
                              <span>{p.clothing_size} · 👟 {p.shoe_size}</span>
                            </div>
                          ))}
                          {(f.upcoming_needs || []).slice(0, 3).map((n, i) => (
                            <div key={i} className="border-t border-border pt-2 mt-2">
                              <p className="font-medium text-sm">{n.item}</p>
                              <p className="text-muted-foreground">{n.reason}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
                {data.children.length === 0 && (
                  <div className="md:col-span-2 lg:col-span-3 border border-dashed border-border p-12 text-center">
                    <p className="font-serif text-2xl">No children yet.</p>
                    <p className="text-sm text-muted-foreground mt-2">Add a profile to unlock growth forecasts and seasonal needs.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="eyebrow">{label}</Label><div className="mt-1.5">{children}</div></div>;
}
